// ota.js — self-hosted over-the-air (OTA) web-bundle updates for the STAFF
// Capacitor tablet app (tech.healther.opd). Native-only; a complete no-op in a
// web browser tab (the web build ships by rsync, never OTA).
//
// HOW IT FITS TOGETHER
//   • scripts/build-ota-bundles.mjs produces ota-dist/staff/{manifest.json,
//     <version>.zip}; Express serves them at /ota/staff/… (server/index.mjs).
//   • The plugin's phone-home is fully neutralised in capacitor.config.json
//     (autoUpdate:false + updateUrl/statsUrl/channelUrl all ""), so NOTHING here
//     talks to Capgo's cloud — we drive every step manually against OUR server.
//
// THE FLOW (startOtaUpdates, called once from main.jsx on native):
//   1. Call notifyAppReady() immediately, before config or network work. Capgo
//      8.50.2 defines readiness narrowly as "the bundle JavaScript initialized";
//      delaying it behind I/O causes healthy bundles to roll back when offline.
//   2. Check for an update on boot, and again on every app resume. A failed fetch
//      is the network authority; navigator.onLine is advisory and is not used.
//   3. Fetch /ota/staff/manifest.json (no-store) via net-base's serverBase, so
//      the https://localhost WebView reaches the real Express origin.
//   4. If manifest.version ≠ the running bundle version → download({url,version,
//      checksum}) then next({id}). `next` applies on the NEXT launch/background —
//      NEVER a mid-session hot reload (a doctor may be mid-consult). The plugin
//      verifies the zip's SHA-256 against `checksum` natively (CryptoCipher =
//      SHA-256), so we do not re-verify in JS.
//
// FAILURE POSTURE (AGENTS §1.3 fail-loud where it matters):
//   • Network/offline errors → quiet (expected). We just try again next resume.
//   • Integrity/apply errors (checksum mismatch, download/next throw) → LOUD:
//     console.error + a window 'ota:error' CustomEvent so the app/telemetry can
//     surface a corrupt-bundle condition instead of silently limping.

import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isCapacitorNative } from './capacitor-env.js';
import { assetUrl, getServerBase, setServerBase } from './net-base.js';

// serverBase is normally plumbed by medplum-client.js appBundle() — but that
// runs LAZILY on the first screen that needs Medplum, which on a cold boot to
// the login screen can be AFTER our boot check fires. So if it isn't set yet,
// resolve it ourselves from the same baked config the app reads
// (/assets/medplum-demo.json in the WebView bundle) — idempotent with the app's
// own later call. Without this, the manifest fetch would resolve against the
// https://localhost WebView origin and silently 404.
let serverBaseEnsured = null;

export function normalizeTabletServerBase(value) {
  const raw = String(value || '').trim();
  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    throw new Error('packaged tablet config has no valid absolute serverBase origin');
  }
  if (!['http:', 'https:'].includes(parsed.protocol)
      || !parsed.host
      || parsed.username
      || parsed.password
      || parsed.pathname !== '/'
      || parsed.search
      || parsed.hash) {
    throw new Error('packaged tablet config serverBase must be one HTTP(S) origin with no path, credentials, query, or fragment');
  }
  return parsed.origin;
}

async function ensureServerBase() {
  const existing = getServerBase();
  if (existing) {
    normalizeTabletServerBase(existing);
    return;
  }
  if (!serverBaseEnsured) {
    serverBaseEnsured = (async () => {
      const configUrl = assetUrl('/assets/medplum-demo.json');
      const response = await fetch(configUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`packaged tablet config missing (HTTP ${response.status} at ${configUrl})`);
      }
      const cfg = await response.json();
      const base = normalizeTabletServerBase(cfg && cfg.serverBase);
      if (!getServerBase()) setServerBase(base);
    })();
  }
  await serverBaseEnsured;
}

function reportOtaError(phase, error, version) {
  const message = String(error && error.message || error);
  console.error(`[ota] ${phase} FAILED:`, error);
  showOtaError({ phase, version: version || null, message });
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('ota:error', {
      detail: { phase, version: version || null, message },
    }));
  }
}

function showOtaError({ phase, message }) {
  if (typeof document === 'undefined') return;
  const render = () => {
    if (!document.body) return;
    let banner = document.getElementById('healther-ota-error');
    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'healther-ota-error';
      banner.setAttribute('role', 'alert');
      banner.style.cssText = 'position:fixed;left:16px;right:16px;bottom:16px;z-index:2147483647;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:12px 16px;border:1px solid #b42318;border-radius:10px;background:#fff1f0;color:#7a271a;font:600 14px/1.4 Inter,system-ui,sans-serif;box-shadow:0 8px 24px rgba(122,39,26,.18)';
      const text = document.createElement('span');
      text.dataset.otaErrorText = 'true';
      banner.appendChild(text);
      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.textContent = 'Dismiss';
      dismiss.style.cssText = 'border:0;background:transparent;color:inherit;font:inherit;cursor:pointer;text-decoration:underline';
      dismiss.addEventListener('click', () => banner.remove());
      banner.appendChild(dismiss);
      document.body.appendChild(banner);
    }
    banner.querySelector('[data-ota-error-text]').textContent = `Tablet update ${phase} failed: ${message}`;
  };
  if (document.body) render();
  else document.addEventListener('DOMContentLoaded', render, { once: true });
}

// The manifest lives on OUR Express origin. assetUrl() prefixes serverBase on
// native (same helper the app uses for /assets, /uploads) so this resolves to
// e.g. http://192.168.1.4:8787/ota/staff/manifest.json instead of the
// https://localhost WebView origin.
export function manifestUrl() {
  return assetUrl('/ota/staff/manifest.json');
}

// Resolve a manifest bundle only on the configured hospital-server origin. The
// manifest is server-controlled, but it is still release metadata: accepting a
// foreign absolute URL would let a compromised/misconfigured manifest redirect
// tablets away from the deployment origin. The path prefix also prevents a
// valid same-origin URL from pointing at an unrelated endpoint.
export function bundleUrl(manifest) {
  const raw = manifest && manifest.url ? String(manifest.url).trim() : '';
  const base = normalizeTabletServerBase(getServerBase());
  let parsed;
  try {
    parsed = new URL(raw, `${base}/`);
  } catch {
    throw new Error('ota bundle URL is invalid');
  }
  if (!['http:', 'https:'].includes(parsed.protocol)
      || parsed.origin !== base
      || parsed.username
      || parsed.password
      || !parsed.pathname.startsWith('/ota/staff/')
      || parsed.search
      || parsed.hash) {
    throw new Error('ota bundle URL must be an unqualified /ota/staff/ path on the configured server origin');
  }
  return parsed.href;
}

// Pure decision helper (unit-tested): should we download this manifest given the
// currently-running bundle version? True only when the manifest is well-formed
// and names a different version than what is live.
function isValidManifest(manifest) {
  return !!manifest
    && typeof manifest.version === 'string'
    && !!manifest.version
    && typeof manifest.sha256 === 'string'
    && /^[a-f0-9]{64}$/i.test(manifest.sha256)
    && typeof manifest.url === 'string'
    && !!manifest.url;
}

export function shouldUpdate(currentVersion, manifest) {
  return isValidManifest(manifest) && manifest.version !== currentVersion;
}

async function fetchManifest() {
  // no-store so a tablet never serves a cached pointer. Only a transport failure
  // is classified as offline; an HTTP or JSON failure is a broken release
  // surface and must remain distinguishable in logs/telemetry.
  let res;
  try {
    res = await fetch(manifestUrl(), { cache: 'no-store' });
  } catch (error) {
    error.otaTransportFailure = true;
    throw error;
  }
  if (res.status === 404) return null;         // box has no bundle yet — not an error
  if (!res.ok) throw new Error(`ota manifest HTTP ${res.status}`);
  let manifest;
  try {
    manifest = await res.json();
  } catch (error) {
    throw new Error(`ota manifest is not valid JSON (${error.message})`);
  }
  if (!isValidManifest(manifest)) {
    throw new Error('ota manifest is missing a valid version, URL, or SHA-256 checksum');
  }
  return manifest;
}

let checking = false; // guard against overlapping checks (boot + resume racing)

export async function checkForUpdate() {
  if (checking) return { status: 'busy' };
  checking = true;
  try {
    try {
      await ensureServerBase();
    } catch (configError) {
      // This file is part of the installed web bundle. Missing/invalid config is
      // release corruption, not an expected clinic network outage; surface it.
      reportOtaError('configuration', configError);
      return { status: 'configuration-error', message: String(configError && configError.message || configError) };
    }

    let manifest;
    try {
      console.info('[ota] checking', manifestUrl());
      manifest = await fetchManifest();
    } catch (netErr) {
      if (netErr?.otaTransportFailure) {
        // Network error = fail QUIET (expected). Info-level so a stuck field
        // tablet is diagnosable from logcat without being noisy.
        console.info('[ota] manifest fetch failed (offline):', netErr.message);
        return { status: 'offline' };
      }
      reportOtaError('manifest', netErr);
      return { status: 'manifest-error', message: String(netErr?.message || netErr) };
    }
    if (!manifest) { console.info('[ota] no bundle published yet'); return { status: 'no-bundle' }; }

    let cur;
    try {
      cur = await CapacitorUpdater.current();
    } catch (error) {
      reportOtaError('current-bundle', error);
      return { status: 'current-bundle-error', message: String(error?.message || error) };
    }
    const currentVersion = (cur && cur.bundle && cur.bundle.version) || '';
    if (!shouldUpdate(currentVersion, manifest)) {
      console.info(`[ota] up-to-date (running ${currentVersion})`);
      return { status: 'up-to-date', version: currentVersion };
    }
    console.info(`[ota] update available: ${currentVersion} -> ${manifest.version}`);

    // ── Integrity-critical from here: fail LOUD on any error ──
    try {
      const bundle = await CapacitorUpdater.download({
        url: bundleUrl(manifest),
        version: manifest.version,
        checksum: manifest.sha256, // native SHA-256 verification (unencrypted bundle)
      });
      // Apply on next launch/background — never mid-session.
      await CapacitorUpdater.next({ id: bundle.id });
      console.info(`[ota] downloaded ${manifest.version}; will apply on next launch`);
      return { status: 'downloaded', version: manifest.version, bundleId: bundle.id };
    } catch (applyErr) {
      reportOtaError('download', applyErr, manifest.version);
      return { status: 'error', version: manifest.version };
    }
  } finally {
    checking = false;
  }
}

let otaStartup = null;

// Wire the whole thing up. The returned promise makes readiness ordering
// observable in tests; main.jsx intentionally does not await it, so first paint
// is never blocked.
export function startOtaUpdates() {
  if (!isCapacitorNative()) return Promise.resolve({ status: 'web' });
  if (otaStartup) return otaStartup;
  let readiness;
  try {
    // This native call MUST be initiated before any config fetch or other I/O.
    readiness = CapacitorUpdater.notifyAppReady();
  } catch (error) {
    reportOtaError('readiness', error);
    otaStartup = Promise.resolve({ status: 'readiness-error', message: String(error?.message || error) });
    return otaStartup;
  }
  otaStartup = Promise.resolve(readiness).then(() => {
    // Check on boot (deferred so it never blocks first paint / login).
    setTimeout(() => { void checkForUpdate(); }, 3000);

    // Check again whenever the app returns to the foreground.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void checkForUpdate();
      });
    }
    return { status: 'ready' };
  }, (error) => {
    reportOtaError('readiness', error);
    return { status: 'readiness-error', message: String(error?.message || error) };
  });
  return otaStartup;
}
